import { useCallback, useEffect, useState } from "react";
import { prettifyApiError } from "@/shared/api";
import {
  anteprimaSost,
  riordinaSost,
  type AnteprimaSost,
  type EsitoSost,
  type FornitoreRiordino,
} from "../riordinaSostApi";
import { IconAlert, IconCheck, IconPrint } from "./icons";

const euro = (n: number): string =>
  n.toLocaleString("it-IT", { style: "currency", currency: "EUR" });

/**
 * Popup «Riordina» (Davide 2026-09-24). Il popup E' la conferma: dice a
 * chi riparte, con che numero e che etichetta, e chiede cosa si sostituisce e
 * — dove non lo dice il portale — il costo IVATO, che va nel registro e in BI.
 *
 * L'anteprima si carica all'apertura: se l'etichetta della sostituzione non si
 * puo' creare (colli senza peso) o il portale non trova uno SKU, l'errore
 * compare QUI, prima di spendere. Se manca, la crea l'invio (Davide 2026-10-05).
 * Testo nero, gerarchia con peso e dimensione (Davide 2026-09-23).
 */
export function RiordinaSostDialog({
  orderRef,
  fornitore,
  defaultWhat,
  onDone,
  onCancel,
}: {
  orderRef: string;
  fornitore: FornitoreRiordino;
  defaultWhat: string;
  onDone: (riferimento: string) => void;
  onCancel: () => void;
}) {
  const [anteprima, setAnteprima] = useState<AnteprimaSost | null>(null);
  const [what, setWhat] = useState(defaultWhat);
  // SKU → quantita' che riparte (0 = esclusa). Parte da tutto l'ordine: per
  // rispedire una scarpiera su due si toglie, non si aggiunge (Davide 2026-10-12).
  const [scelte, setScelte] = useState<Record<string, number>>({});
  const [cost, setCost] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Partito ma col costo non registrato: si resta qui a dirlo, e «Chiudi»
  // aggiorna la lista come un successo — l'ordine al fornitore C'E'.
  const [partito, setPartito] = useState<EsitoSost | null>(null);

  useEffect(() => {
    let vivo = true;
    anteprimaSost(orderRef, fornitore.id)
      .then((a) => {
        if (!vivo) return;
        setAnteprima(a);
        setScelte(Object.fromEntries(a.righe.map((r) => [r.sku, r.quantita])));
      })
      .catch((err: unknown) => vivo && setError(prettifyApiError(err)));
    return () => {
      vivo = false;
    };
  }, [orderRef, fornitore.id]);

  // Chiudere dopo un ordine partito aggiorna la lista (onDone), non annulla.
  const chiudi = useCallback(
    () => (partito ? onDone(partito.riferimento) : onCancel()),
    [partito, onDone, onCancel],
  );

  // ESC chiude, come gli altri dialog; ignorato mentre l'ordine parte.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) chiudi();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, chiudi]);

  const chiedeCosto = anteprima !== null && !anteprima.costo_dal_portale;
  const costNumber = Number(cost.replace(",", "."));
  const costValid =
    !chiedeCosto || (cost.trim() !== "" && Number.isFinite(costNumber) && costNumber >= 0);
  const scegli = anteprima?.scelta_righe === true;
  const righeScelte = Object.fromEntries(Object.entries(scelte).filter(([, q]) => q > 0));
  const nessunaRiga = scegli && Object.keys(righeScelte).length === 0;
  const canConfirm =
    anteprima !== null &&
    !nessunaRiga &&
    !anteprima.gia_inviato &&
    !partito &&
    what.trim() !== "" &&
    costValid &&
    !busy;

  async function conferma() {
    if (!anteprima) return;
    setBusy(true);
    setError(null);
    try {
      const esito = await riordinaSost(orderRef, {
        fornitore: fornitore.id,
        what: what.trim(),
        costGross: chiedeCosto ? costNumber : null,
        ...(scegli ? { righe: righeScelte } : {}),
      });
      if (esito.avviso) setPartito(esito);
      else onDone(esito.riferimento);
    } catch (err) {
      setError(prettifyApiError(err));
    } finally {
      setBusy(false);
    }
  }

  const etichettaStato = anteprima?.etichetta_stato ?? (anteprima?.etichetta ? "pronta" : null);
  const creaEtichetta = etichettaStato === "da_creare";

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-ink/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="riordina-sost-titolo"
      data-no-open
      onClick={(e) => {
        // Il click non deve aprire la card sotto; sul fondo = annulla.
        e.stopPropagation();
        if (e.target === e.currentTarget && !busy) chiudi();
      }}
    >
      <div className="flex max-h-[calc(100dvh-2rem)] w-full max-w-lg flex-col overflow-hidden rounded-lg border border-line bg-card text-ink shadow-popover">
        <header className="border-b border-line px-5 pb-4 pt-5">
          <h3
            id="riordina-sost-titolo"
            className="font-serif text-[18px] font-semibold leading-tight"
          >
            Riordina come sostituzione
          </h3>
          <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[14px]">
            <span>
              A <strong>{fornitore.nome}</strong>, numero
            </span>
            <span className="rounded-sm border border-violet bg-violet-soft px-2 py-0.5 font-mono text-[13px] font-semibold">
              {anteprima?.riferimento ?? "…"}
            </span>
          </p>
          {anteprima && anteprima.precedenti > 0 && (
            <p className="mt-2 text-[13px]">
              <strong>Sostituzione n. {anteprima.precedenti + 1}</strong> a {fornitore.nome} per
              quest'ordine:{" "}
              {anteprima.precedenti === 1
                ? "1 è già partita"
                : `${anteprima.precedenti} sono già partite`}
              .
            </p>
          )}
        </header>

        <div className="flex flex-col gap-4 overflow-y-auto px-5 py-4 text-[13px]">
          {!anteprima && !error && (
            <div aria-live="polite" aria-busy="true" className="flex flex-col gap-2">
              <span className="sr-only">Leggo cosa ripartirebbe…</span>
              <div className="h-4 w-2/3 rounded-sm bg-paper-2 motion-safe:animate-pulse" />
              <div className="h-10 rounded-md bg-paper-2 motion-safe:animate-pulse" />
              <div className="h-12 rounded-md bg-paper-2 motion-safe:animate-pulse" />
            </div>
          )}

          {anteprima && (
            <section aria-labelledby="riordina-sost-righe">
              <h4 id="riordina-sost-righe" className="mb-1.5 text-[13px] font-semibold">
                Cosa riparte
              </h4>
              {scegli && (
                <p className="mb-1.5 text-[12px]">
                  Togli la spunta o abbassa la quantità per riordinare solo una parte.
                </p>
              )}
              <ul className="divide-y divide-line rounded-md border border-line">
                {anteprima.righe.map((r) =>
                  scegli ? (
                    <RigaScelta
                      key={r.sku}
                      sku={r.sku}
                      massimo={r.quantita}
                      quantita={scelte[r.sku] ?? 0}
                      disabled={busy || !!partito}
                      onChange={(q) => setScelte((s) => ({ ...s, [r.sku]: q }))}
                    />
                  ) : (
                    <li key={r.sku} className="flex items-center justify-between gap-3 px-3 py-2">
                      <span className="min-w-0 break-all font-mono">{r.sku}</span>
                      <span className="flex shrink-0 items-center gap-3 font-mono tabular-nums">
                        <span>× {r.quantita}</span>
                        {r.prezzo !== null && <strong>{euro(r.prezzo)}</strong>}
                      </span>
                    </li>
                  ),
                )}
                {anteprima.totale_ivato !== null && (
                  <li className="flex items-center justify-between gap-3 bg-paper px-3 py-2">
                    <span>Totale IVA inclusa (dal portale)</span>
                    <strong className="font-mono tabular-nums">
                      {euro(anteprima.totale_ivato)}
                    </strong>
                  </li>
                )}
              </ul>
            </section>
          )}

          {nessunaRiga && <Avviso tono="amber">Spunta almeno una riga da riordinare.</Avviso>}

          {anteprima && etichettaStato && (
            <div
              data-testid="riordina-sost-etichetta"
              className={`flex items-start gap-3 rounded-md border px-3 py-2.5 ${
                creaEtichetta ? "border-violet bg-violet-soft" : "border-green bg-green-soft"
              }`}
            >
              <span
                className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white ${
                  creaEtichetta ? "bg-violet" : "bg-green"
                }`}
              >
                {creaEtichetta ? (
                  <IconPrint className="h-4 w-4 fill-none stroke-current stroke-[1.8] [stroke-linecap:round] [stroke-linejoin:round]" />
                ) : (
                  <IconCheck className="h-4 w-4 fill-none stroke-current stroke-[2.6] [stroke-linecap:round] [stroke-linejoin:round]" />
                )}
              </span>
              <div className="min-w-0">
                <p className="font-semibold">
                  {creaEtichetta
                    ? "Nuova etichetta (SDA in Italia, BRT all'estero)"
                    : "Etichetta della sostituzione pronta"}
                </p>
                <p className="mt-0.5">
                  {creaEtichetta
                    ? "OM la crea alla conferma e la allega: tracking nuovo, mai la LDV originale."
                    : "Viene allegata all'ordine al fornitore."}
                </p>
                {anteprima.etichetta && (
                  <p className="mt-1 break-all font-mono text-[12px]">{anteprima.etichetta}</p>
                )}
              </div>
            </div>
          )}

          {anteprima?.gia_inviato && (
            <Avviso tono="red">
              Questa sostituzione risulta già ordinata a {fornitore.nome}: non riparte.
            </Avviso>
          )}
          {anteprima?.avvisi.map((a) => (
            <Avviso key={a} tono="amber">
              {a}
            </Avviso>
          ))}

          {anteprima && !anteprima.gia_inviato && (
            <div className="flex flex-col gap-3">
              <label className="flex flex-col gap-1 text-[13px] font-semibold">
                Cosa sostituisci
                <input
                  type="text"
                  value={what}
                  onChange={(e) => setWhat(e.target.value)}
                  disabled={busy}
                  autoFocus
                  className="h-[44px] rounded-md border border-line bg-card px-3 text-[14px] font-normal text-ink outline-none focus:border-violet focus:ring-2 focus:ring-violet-soft disabled:bg-paper"
                  placeholder="Es. gamba tavolo, pannello laterale…"
                />
                <span className="text-[12px] font-normal">Finisce nel registro e nella BI.</span>
              </label>
              {chiedeCosto && (
                <label className="flex flex-col gap-1 text-[13px] font-semibold">
                  Costo ivato (€)
                  <input
                    type="text"
                    inputMode="decimal"
                    value={cost}
                    onChange={(e) => setCost(e.target.value)}
                    disabled={busy}
                    className="h-[44px] w-40 rounded-md border border-line bg-card px-3 font-mono text-[14px] font-normal tabular-nums text-ink outline-none focus:border-violet focus:ring-2 focus:ring-violet-soft disabled:bg-paper"
                    placeholder="0,00"
                  />
                  <span className="text-[12px] font-normal">IVA inclusa; 0 se è gratuita.</span>
                </label>
              )}
            </div>
          )}

          {error && <Avviso tono="red">{error}</Avviso>}
          {partito && <Avviso tono="amber">{partito.avviso}</Avviso>}
        </div>

        <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-line bg-paper px-5 py-3">
          <button
            type="button"
            onClick={chiudi}
            disabled={busy}
            className="min-h-[44px] rounded-md border border-line bg-card px-4 text-[13px] font-medium text-ink hover:border-ink disabled:cursor-not-allowed disabled:opacity-50"
          >
            {partito ? "Chiudi" : "Annulla"}
          </button>
          <button
            type="button"
            disabled={!canConfirm}
            aria-busy={busy}
            onClick={() => void conferma()}
            className="min-h-[44px] rounded-md border border-violet-2 bg-violet px-4 text-[13px] font-semibold text-white transition-colors hover:bg-violet-2 disabled:cursor-not-allowed disabled:border-line disabled:bg-line disabled:text-ink"
          >
            {busy
              ? creaEtichetta
                ? "Creo l'etichetta e riordino…"
                : "Riordino…"
              : creaEtichetta
                ? "Crea etichetta e riordina"
                : "Conferma e riordina"}
          </button>
        </footer>
      </div>
    </div>
  );
}

/** Riga con spunta e quantita' (1…ordinata, coi tasti − e +): solo dove il
 *  fornitore accetta un riordino parziale. Rispuntare riparte dal massimo. */
function RigaScelta({
  sku,
  massimo,
  quantita,
  disabled,
  onChange,
}: {
  sku: string;
  massimo: number;
  quantita: number;
  disabled: boolean;
  onChange: (q: number) => void;
}) {
  const inclusa = quantita > 0;
  return (
    <li className={`flex items-center gap-3 px-3 py-1 ${inclusa ? "" : "bg-paper"}`}>
      <label className="flex min-h-[44px] min-w-0 flex-1 cursor-pointer items-center gap-3">
        <input
          type="checkbox"
          checked={inclusa}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked ? massimo : 0)}
          className="h-[18px] w-[18px] shrink-0 cursor-pointer accent-violet"
        />
        <span className={`min-w-0 break-all font-mono ${inclusa ? "" : "line-through"}`}>
          {sku}
        </span>
      </label>
      {massimo > 1 ? (
        <div
          role="group"
          aria-label={`Quantità di ${sku}`}
          className="flex shrink-0 items-center gap-1 font-mono tabular-nums"
        >
          <Passo
            label={`Una ${sku} in meno`}
            disabled={disabled || !inclusa || quantita <= 1}
            onClick={() => onChange(quantita - 1)}
          >
            −
          </Passo>
          <output aria-live="polite" className="min-w-[56px] px-1 text-center text-[14px]">
            {inclusa ? quantita : 0} di {massimo}
          </output>
          <Passo
            label={`Una ${sku} in più`}
            disabled={disabled || !inclusa || quantita >= massimo}
            onClick={() => onChange(quantita + 1)}
          >
            +
          </Passo>
        </div>
      ) : (
        <span className="shrink-0 font-mono tabular-nums">× 1</span>
      )}
    </li>
  );
}

function Passo({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex h-[44px] w-[44px] items-center justify-center rounded-md border border-line bg-card text-[18px] text-ink hover:border-ink disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}

/** Riquadro di avviso o errore: testo nero (Davide 2026-09-23), il tono lo
 *  danno sfondo, bordo e icona. */
function Avviso({ tono, children }: { tono: "red" | "amber"; children: React.ReactNode }) {
  return (
    <div
      role={tono === "red" ? "alert" : "status"}
      className={`flex items-start gap-2.5 rounded-md border px-3 py-2.5 text-[13px] font-medium ${
        tono === "red" ? "border-red bg-red-soft" : "border-amber bg-amber-soft"
      }`}
    >
      <IconAlert
        className={`mt-px h-[18px] w-[18px] shrink-0 fill-none stroke-[1.8] [stroke-linecap:round] [stroke-linejoin:round] ${
          tono === "red" ? "stroke-red" : "stroke-amber"
        }`}
      />
      <div className="min-w-0">{children}</div>
    </div>
  );
}
